import {
  CurrentStatus,
  DayTime,
  ScheduleType,
  WeeklySchedule,
} from "@/custom-types/DispensarySchedules";
import { Weekday } from "@/custom-types/Weekday";

// today's weekday name
export const getTodayWeekday = (): Weekday =>
  new Date()
    .toLocaleDateString("en-US", {
      weekday: "long",
    })
    .toLowerCase() as Weekday;

export const getTomorrowWeekday = (): Weekday => {
  const now = new Date();
  now.setDate(now.getDate() + 1);
  return now
    .toLocaleDateString("en-US", {
      weekday: "long",
    })
    .toLowerCase() as Weekday;
};

const weekDays: Weekday[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const timeZoneAbbr: { [key: string]: string } = {
  ADT: "AT",
  AKDT: "AKT",
  AKST: "AKT",
  AST: "AT",
  CDT: "CT",
  CST: "CT",
  EDT: "ET",
  EST: "ET",
  HDT: "HT",
  HST: "HT",
  MDT: "MT",
  MST: "MT",
  PDT: "PT",
  PST: "PT",
};

export const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getShortenedTimeZone = (storeTimeZone: string) => {
  if (storeTimeZone === userTimeZone) {
    return null;
  }

  if (timeZoneAbbr[storeTimeZone]) {
    return timeZoneAbbr[storeTimeZone];
  }

  const timeZoneWithDate = new Intl.DateTimeFormat("en-US", {
    timeZone: storeTimeZone,
    timeZoneName: "short",
  }).format();

  const timeZoneWithDaylightOrStandard = timeZoneWithDate.split(" ")[1];

  return timeZoneAbbr[timeZoneWithDaylightOrStandard];
};

// formats military time to standard time and adds AM/PM
export const formatTime = (time?: string) => {
  if (!time) {
    return null;
  }

  // get individual time parts and convert them to integers
  const [hour, minute] = time.split(":").map(Number);
  const hourString = hour === 0 ? 12 : hour;
  const minuteString = minute === 0 ? "" : `:${minute}`;
  const isPm = hour >= 12;

  if (isPm && hour === 12) {
    return `${hourString}${minuteString}pm`;
  }

  return isPm
    ? `${hourString - 12}${minuteString}pm`
    : `${hourString}${minuteString}am`;
};

export const formatDay = (day: string) => {
  return day.charAt(0).toUpperCase() + day.slice(1);
};

const getClosedMessage = (
  dayDisplay: string,
  time: string | null | undefined,
  shortenedTimeZone: string | null,
  isSpecialHours?: boolean,
) => {
  return time
    ? `Closed until ${dayDisplay} ${formatTime(time)} ${
        shortenedTimeZone ? shortenedTimeZone : ""
      }${isSpecialHours ? " (Special Hours)" : ""}`
    : null;
};

const getOpenMessage = (
  dayDisplay: string | null,
  time: string | null | undefined,
  isStore: boolean,
  shortenedTimeZone: string | null,
  isSpecialHours?: boolean,
) =>
  time
    ? `${isStore ? `Open` : `available`} until ${dayDisplay}${formatTime(
        time,
      )} ${shortenedTimeZone ? shortenedTimeZone : ""}${isSpecialHours ? " (Special Hours)" : ""}`
    : null;

export const getDeliveryStatusMessage = (
  currentDeliveryStatus?: CurrentStatus | null,
) => {
  if (!currentDeliveryStatus) {
    return "Not available";
  }

  const isCurrentlyOpen = currentDeliveryStatus?.isOpen;

  if (isCurrentlyOpen) {
    return "";
  }

  const statusChangeAtDay = currentDeliveryStatus?.statusChangeAt?.day;
  const isOpenLaterToday = statusChangeAtDay === getTodayWeekday();

  if (isOpenLaterToday) {
    const startTime = currentDeliveryStatus?.statusChangeAt?.time;
    return `Starts at ${formatTime(startTime)}`;
  }

  const isOpenTomorrow = statusChangeAtDay === getTomorrowWeekday();

  if (isOpenTomorrow) {
    return "Starts tomorrow";
  }

  const formattedOpenDay = formatDay(statusChangeAtDay);

  return `Starts ${formattedOpenDay}`;
};

// returns a formatted status message for store, pickup, or delivery schedules
export const getStatusMessage = (
  hasPreorder: boolean,
  status: CurrentStatus | null | undefined,
  storeTimeZone: string | undefined,
  type: "store" | "pickup" | "delivery",
  isSpecialHours?: boolean,
) => {
  const { isOpen, isOpen24Hours, statusChangeAt } = status ?? {};

  if (isOpen === null || statusChangeAt === null || !storeTimeZone) {
    return null;
  }

  // if the schedule is open/available 24 hours, short-circuit and return 24 hours messaging
  if (isOpen && isOpen24Hours) {
    return `${type === "store" ? "Open" : "Available"} 24 hours${isSpecialHours ? " (Special Hours)" : ""}`;
  }

  const statusChangeAtDay = statusChangeAt?.day ?? "";
  // returns "tomorrow" or day of the week for the message
  const statusChangeDay = `${
    statusChangeAt?.day === getTomorrowWeekday()
      ? "tomorrow"
      : formatDay(statusChangeAtDay)
  }`;

  const shortenedTimeZone = getShortenedTimeZone(storeTimeZone);

  // schedule is open
  if (isOpen) {
    return getOpenMessage(
      statusChangeAt?.day !== getTodayWeekday() ? `${statusChangeDay} at ` : "",
      statusChangeAt?.time,
      type === "store",
      shortenedTimeZone,
      isSpecialHours,
    );
  }

  // schedule type is pickup or store, schedule is closed and preorder is available
  if (type !== "delivery" && !isOpen && hasPreorder) {
    return `Preorder until ${
      statusChangeDay.toLowerCase() === getTodayWeekday()
        ? ``
        : `${statusChangeDay} at `
    }${formatTime(statusChangeAt?.time)} ${
      shortenedTimeZone ? shortenedTimeZone : ""
    }`;
  }

  // schedule is closed
  return getClosedMessage(
    statusChangeAt?.day !== getTodayWeekday() ? `${statusChangeDay} at ` : "",
    statusChangeAt?.time,
    shortenedTimeZone,
    isSpecialHours,
  );
};

// returns an array of days with open/close times for a given schedule type
export const formatSchedule = (
  unformattedSchedule: WeeklySchedule,
  scheduleType: ScheduleType,
  opts?: { todayFirst: boolean },
) => {
  const formattedSchedule = Object.entries(unformattedSchedule).map(
    ([day, schedule]) => {
      const { open, close, isSpecialHours } = schedule[scheduleType] || {};

      const openTime = open && formatTime(open.time);
      const closeTime = close && formatTime(close.time);
      const isOpen24Hours = getIsOpen24Hours(open, close);
      return {
        close: { ...close, time: closeTime },
        day,
        isOpen24Hours,
        isSpecialHours,
        open: { ...open, time: openTime },
      };
    },
  );

  // use the index of today to rotate the schedule so that today is first
  if (opts?.todayFirst) {
    const todaysDayOfTheWeek = new Date()
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    const todaysIndex = formattedSchedule.findIndex(
      ({ day }) => day === todaysDayOfTheWeek,
    );

    if (todaysIndex > 0) {
      const arrayToPivot = [...formattedSchedule];
      const prependedDays = arrayToPivot.splice(todaysIndex);
      return prependedDays.concat(arrayToPivot);
    }
  }

  return formattedSchedule;
};

export const getIsSpecialHours = (
  schedule: WeeklySchedule | null,
  scheduleType: ScheduleType,
  day: Weekday,
): boolean => {
  const daySchedule = schedule?.[day]?.[scheduleType];
  return !!daySchedule?.isSpecialHours;
};

export const getIsOpen24Hours = (
  open: DayTime | null | undefined,
  close: DayTime | null | undefined,
): boolean => {
  if (!open || !close) {
    return false;
  }

  const openIndex = weekDays.indexOf(open.day);
  const closeIndex = weekDays.indexOf(close.day);
  const closeOneDayLater = (openIndex + 1) % 7 === closeIndex;

  return open.time === "00:00" && close.time === "00:00" && closeOneDayLater;
};
